import { useState, useEffect, useRef } from 'react';

/**
 * スキルタグ関連の共通ロジックを管理するカスタムフック
 * @param {Object} options - 設定オプション
 * @param {Array} options.selectedTags - 選択済みタグの配列
 * @param {Function} options.setSelectedTags - 選択済みタグを更新する関数
 * @param {string} options.mode - 動作モード ('editor' | 'filter')
 * @returns {Object} スキルタグ管理に必要な状態と関数
 */
export const useSkillTags = ({ selectedTags, setSelectedTags, tagCategories, mode}) => {
    // 共通の状態管理
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [inputValue, setInputValue] = useState(''); // 入力フィールドの値
    const [filteredTags, setFilteredTags] = useState(null); // フィルタリングされたタグ候補
    const [tempSelectedTags, setTempSelectedTags] = useState([]); // モーダル内での一時的な選択状態

    
    // フィルター用の追加状態
    const [isOpenSearchList, setIsOpenSearchList] = useState(false);
    const searchContainerRef = useRef(null);

    // フィルター用：外部クリック検知で検索候補を閉じる
    useEffect(() => {
        if (mode !== 'filter') return; 
        
        const handleClickOutside = (event) => {
            if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
                setIsOpenSearchList(false);
            }
        };

        if (isOpenSearchList) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpenSearchList, mode]);

    /**
     * タグのフィルタリングを行う関数
     * @param {string} input - 入力文字列
     */
    const filterTags = (input) => {
        if (!input.trim()) {
            setFilteredTags([]);
            return;
        }
        
        const lowerInput = input.toLowerCase();
        
        // 入力に基づいてタグを検索
        // フィルターモードでは現在の選択状態とモーダル開閉状態を考慮
        // エディターモードでは一時的な選択状態を考慮
        const excludedTags = mode === 'filter' 
            ? (isModalOpen ? tempSelectedTags : selectedTags)
            : tempSelectedTags;

        const allTags = tagCategories ? Object.values(tagCategories).flat() : [];
            
        const matchingTags = allTags.filter(tag => {
            return tag.toLowerCase().includes(lowerInput) && !excludedTags.includes(tag);
        });
        
        // 類似度に基づいてソート
        matchingTags.sort((a, b) => {
            const aLower = a.toLowerCase();
            const bLower = b.toLowerCase();
            
            // 完全一致を最優先
            if (aLower === lowerInput) return -1;
            if (bLower === lowerInput) return 1;
            
            // 前方一致を次に優先
            const aStartsWith = aLower.startsWith(lowerInput);
            const bStartsWith = bLower.startsWith(lowerInput);
            
            if (aStartsWith && !bStartsWith) return -1;
            if (!aStartsWith && bStartsWith) return 1;
            
            return aLower.indexOf(lowerInput) - bLower.indexOf(lowerInput);
        });
        
        setFilteredTags(matchingTags);
    };

    /**
     * 入力フィールドの変更を処理する関数
     * @param {Event} e - イベントオブジェクト
     */
    const handleInputChange = (e) => {
        const value = e.target.value;
        setInputValue(value);
        
        // 入力に基づいてタグをフィルタリング
        filterTags(value);
        
        // フィルターモードの場合は検索候補の表示制御
        if (mode === 'filter') {
            if (value.trim()) {
                setIsOpenSearchList(true);
            } else {
                setIsOpenSearchList(false);
            }
        }
    };

    /**
     * タグを追加/削除する関数（PC版フィルター用）
     * @param {string} tag - 操作対象のタグ
     */
    const toggleTag = (tag) => {
        if (selectedTags.includes(tag)) {
            // 既に選択されている場合は削除
            setSelectedTags(selectedTags.filter(t => t !== tag));
        } else {
            // 選択されていない場合は追加
            setSelectedTags([...selectedTags, tag]);
        }
    };

    /**
     * SP版モーダル内でのタグ操作（一時的な選択状態）
     * @param {string} tag - 操作対象のタグ
     */
    const toggleTagSP = (tag) => {
        if (tempSelectedTags.includes(tag)) {
            // 既に選択されている場合は削除
            setTempSelectedTags(tempSelectedTags.filter(t => t !== tag));
        } else {
            // 選択されていない場合は追加
            setTempSelectedTags([...tempSelectedTags, tag]);
        }
    };

    /**
     * タグを追加する関数
     * @param {string} tag - 追加するタグ
     */
    const addTag = (tag) => {
        if (!selectedTags.includes(tag)) {
            setSelectedTags([...selectedTags, tag]);
        }
    };

    /**
     * タグを削除する関数
     * @param {string} tagToRemove - 削除するタグ
     */
    const removeTag = (tagToRemove) => {
        setSelectedTags(selectedTags.filter(tag => tag !== tagToRemove));
    };

    /**
     * Enterキーを押した時にタグを追加
     * @param {KeyboardEvent} e - キーボードイベント
     */
    const handleInputKeyDown = (e) => {
        if (e.key === 'Enter' && inputValue.trim()) {
            if (isModalOpen) {
                toggleTagSP(inputValue.trim());
            } else {
                addTag(inputValue.trim());
            }
            setInputValue('');
            setFilteredTags([]);
            e.preventDefault();
        }
    };

    /**
     * 入力フィールドのフォーカス処理（フィルター用）
     */
    const handleInputFocus = () => {
        if (mode === 'filter' && inputValue.trim()) {
            setIsOpenSearchList(true);
        }
    };

    /**
     * 入力フィールドのブラー処理（フィルター用）
     * @param {Event} e - イベントオブジェクト
     */
    const handleInputBlur = (e) => {
        if (mode !== 'filter') return;
        
        // タグ選択エリア内のクリックかチェック
        const tagCategoriesElement = searchContainerRef.current?.querySelector('.tag-categories');
        if (tagCategoriesElement && tagCategoriesElement.contains(e.relatedTarget)) {
            return; // タグ選択エリア内のクリックなら閉じない
        }
        
        // 少し遅延させて、タグクリック時の処理を優先する
        setTimeout(() => {
            setIsOpenSearchList(false);
        }, 150);
    };

    /**
     * モーダルを開く
     */
    const openModal = () => {
        setTempSelectedTags([...selectedTags]); // 現在の選択状態をコピー
        setIsModalOpen(true);
    };

    /**
     * モーダルを閉じる（変更を破棄）
     */
    const closeModal = () => {
        // 閉じるボタンで閉じた場合は変更を破棄
        setIsModalOpen(false);
        setInputValue('');
        setFilteredTags([]);
        setTempSelectedTags([]);
    };

    /**
     * リセット機能
     */
    const handleReset = () => {
        if (isModalOpen) {
            // モーダル内では一時的な選択状態をリセット
            setTempSelectedTags([]);
        } else {
            // PC版では実際の選択状態をリセット
            setSelectedTags([]);
        }
        setInputValue('');
        setFilteredTags([]);
    };

    /**
     * エディター用：完了ボタンの処理
     */
    const handleComplete = () => {
        // 完了ボタンで閉じた場合のみ変更を保存
        setSelectedTags(tempSelectedTags);
        setIsModalOpen(false);
        setInputValue('');
        setFilteredTags([]);
        setTempSelectedTags([]);
    };

    /**
     * フィルター用：検索処理
     */
    const handleSearch = () => {
        setIsOpenSearchList(false);
    };

    /**
     * フィルター用：SP版検索ボタンの処理
     */
    const handleSearchSP = () => {
        // 検索ボタンで閉じた場合は変更を確定
        setSelectedTags(tempSelectedTags);
        // 親コンポーネントのonSearchで実際の検索処理を行う
        setIsModalOpen(false);
        setInputValue('');
        setFilteredTags([]);
        setTempSelectedTags([]);
    };

    /**
     * スキルタグのカテゴリ順序定義
     * API レスポンスを適切な形式に変換する際に使用
     */
    const SKILL_TAG_CATEGORY_ORDER = [
        { key: 'language', name: '言語/フレームワーク' },
        { key: 'db', name: 'データベース' },
        { key: 'infra', name: 'DevOps/インフラ' },
        { key: 'project', name: 'プロジェクト' },
        { key: 'design', name: '設計・アーキテクチャ' }
    ];

    /**
     * スキルタグのAPIレスポンスを適切な形式に変換する内部関数
     * @param {Array} apiData - APIから取得したスキルタグデータ
     * @returns {Object} カテゴリ名をキーとしたスキルタグのオブジェクト
     */
    const transformSkillTagsData = (apiData) => {
        if (!apiData || !Array.isArray(apiData) || apiData.length === 0) {
            return {};
        }

        const skillData = apiData[0]; // 配列の最初の要素を取得
        const transformedData = {};
        
        // 指定した順序でカテゴリを処理
        SKILL_TAG_CATEGORY_ORDER.forEach(({ key, name }) => {
            if (skillData[key] && Array.isArray(skillData[key])) {
                transformedData[name] = skillData[key].map(item => item.name);
            }
        });
        
        return transformedData;
    };

    /**
     * エンジニアデータをスキルタグでフィルタリングする関数
     * @param {Array} engineers - フィルタリング対象のエンジニアデータ配列
     * @param {Array} appliedTags - 適用するタグの配列
     * @returns {Array} フィルタリングされたエンジニアデータ配列
     */
    const filterEngineers = (engineers, appliedTags) => {
        if (!Array.isArray(engineers)) {
            return [];
        }

        return engineers.filter(engineer => {
            // 適用されたタグがない場合は全て表示
            if (!appliedTags || appliedTags.length === 0) {
                return true;
            }
            
            const engineerSkillTags = engineer?.profile?.skillTags || [];
            
            // エンジニアのスキルが適用されたタグのいずれかと一致する場合に表示（OR条件）
            return appliedTags.some(appliedTag => 
                engineerSkillTags.some(skill => 
                    skill.toLowerCase() === appliedTag.toLowerCase()
                )
            );
        });
    };

    return {
        // 状態
        inputValue,
        filteredTags,
        tempSelectedTags,
        isModalOpen,
        isOpenSearchList,
        searchContainerRef,
        
        // 状態更新関数
        setInputValue,
        setFilteredTags,
        setIsOpenSearchList,
        
        // タグ操作関数
        toggleTag,
        toggleTagSP,
        addTag,
        removeTag,
        
        // 入力処理
        handleInputChange,
        handleInputKeyDown,
        handleInputFocus,
        handleInputBlur,
        
        // モーダル管理
        openModal,
        closeModal,
        handleReset,
        
        // モード別の処理
        handleComplete, // エディター用
        handleSearch,   // フィルター用PC版
        handleSearchSP, // フィルター用SP版
        
        // データ変換関数・定数
        transformSkillTagsData,
        SKILL_TAG_CATEGORY_ORDER,
        
        // フィルタリング機能
        filterEngineers,
    };
};
